import { Link, Outlet, useLocation } from 'react-router-dom';
import { ShieldAlert, LayoutGrid } from 'lucide-react';
import { usePermissions } from './usePermissions';
import { evaluateShellRouteAccess, evaluateStandaloneRouteAccess } from './routeAccess';

export function RouteAccessDenied({ featureLabel }: { featureLabel: string }) {
  return (
    <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 px-4 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-950/50 border border-amber-800/50">
        <ShieldAlert className="h-7 w-7 text-amber-500" aria-hidden />
      </div>
      <div className="max-w-md space-y-2">
        <h1 className="text-lg font-semibold vscode-text-primary">Access denied</h1>
        <p className="text-sm vscode-text-secondary">
          Your role does not include <span className="font-medium text-gray-200">{featureLabel}</span>. If you need access,
          ask an organisation owner or admin.
        </p>
      </div>
      <Link
        to="/dashboard"
        className="btn-primary inline-flex items-center gap-2 text-sm"
      >
        <LayoutGrid className="h-4 w-4" aria-hidden />
        Back to dashboard
      </Link>
    </div>
  );
}

/**
 * §10.3 — Wrap `<Outlet />` inside `AppLayout` so every in-shell URL is checked against
 * `SHELL_ROUTE_ACCESS_RULES` (same rules as Sidebar / Topbar visibility).
 */
export function RouteAccessOutletGuard() {
  const { pathname } = useLocation();
  const perms = usePermissions();
  const result = evaluateShellRouteAccess(pathname, perms);
  if (!result.ok) {
    return <RouteAccessDenied featureLabel={result.featureLabel} />;
  }
  return <Outlet />;
}

/**
 * For full-viewport routes outside `AppLayout` (`clients/new`, `settings/offboarding`).
 */
export function StandaloneRouteAccessBoundary({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const perms = usePermissions();
  const result = evaluateStandaloneRouteAccess(pathname, perms);
  if (!result.ok) {
    return <RouteAccessDenied featureLabel={result.featureLabel} />;
  }
  return <>{children}</>;
}
